<template>
	<div
		class="otp-input"
		:class="{ 'otp-input--error': hasError }"
		role="group"
		:aria-label="ariaLabel"
	>
		<input
			v-for="(digit, index) in digits"
			:key="index"
			ref="boxes"
			class="otp-input__box"
			type="text"
			inputmode="numeric"
			pattern="[0-9]*"
			autocorrect="off"
			spellcheck="false"
			:value="digit"
			:disabled="disabled"
			:autocomplete="index === 0 ? 'one-time-code' : 'off'"
			:aria-label="`${ariaLabel} ${index + 1}/${length}`"
			:data-test-id="`${dataTestId}-${index}`"
			@input="onInput(index, $event)"
			@keydown="onKeydown(index, $event)"
			@paste="onPaste(index, $event)"
			@focus="onFocus"
		>
	</div>
</template>

<script>
export default {
	name: 'BaseOtpInput',

	model: {
		prop: 'value',
		event: 'input',
	},

	props: {
		value: {
			type: String,
			default: '',
		},
		length: {
			type: Number,
			default: 6,
		},
		hasError: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		ariaLabel: {
			type: String,
			default: 'Digit',
		},
		dataTestId: {
			type: String,
			default: 'input-code',
		},
	},

	data() {
		return {
			// Held as its own array so a gap (the user clicked box 3 first) keeps
			// rendering in the right slot, even though the value emitted upward is
			// simply the digits joined together.
			// Seeded here rather than through an immediate watcher, which would run
			// before the render function exists and blow up on mount.
			digits: this.toDigits(this.value),
		};
	},

	watch: {
		value(next) {
			if (String(next || '') !== this.digits.join('')) {
				this.digits = this.toDigits(next);
				this.syncBoxes();
			}
		},
		length() {
			this.digits = this.toDigits(this.value);
			this.syncBoxes();
		},
	},

	methods: {
		toDigits(value) {
			const characters = String(value || '').replace(/[^0-9]/g, '').split('');
			return Array.from({ length: this.length }, (item, index) => characters[index] || '');
		},

		// The boxes have no maxlength (so the OS can autofill a whole SMS code into
		// the first one). That means a box can briefly hold more than one character,
		// so push the model back onto the DOM after every change.
		syncBoxes() {
			this.$nextTick(() => {
				(this.$refs.boxes || []).forEach((box, index) => {
					const digit = this.digits[index] || '';

					if (box && box.value !== digit) {
						box.value = digit;
					}
				});
			});
		},

		getBox(index) {
			const boxes = this.$refs.boxes || [];

			return boxes[Math.min(Math.max(index, 0), this.length - 1)] || null;
		},

		focusIndex(index, { select = true } = {}) {
			this.$nextTick(() => {
				const box = this.getBox(index);

				if (!box) {
					return;
				}

				box.focus();

				if (select) {
					box.select();
				}
			});
		},

		focusFirstEmpty() {
			const emptyIndex = this.digits.findIndex((digit) => digit === '');

			this.focusIndex(emptyIndex === -1 ? this.length - 1 : emptyIndex);
		},

		update(digits, focusIndex) {
			this.digits = digits;
			this.syncBoxes();
			this.$emit('input', digits.join(''));

			if (typeof focusIndex === 'number') {
				this.focusIndex(focusIndex);
			}
		},

		isComplete(digits) {
			return digits.length === this.length && digits.every((digit) => digit !== '');
		},

		write(startIndex, characters) {
			const digits = [...this.digits];
			const wasComplete = this.isComplete(this.digits);
			let cursor = startIndex;

			characters.forEach((character) => {
				if (cursor < this.length) {
					digits[cursor] = character;
					cursor += 1;
				}
			});

			this.update(digits, Math.min(cursor, this.length - 1));

			// "Finished entering the code" is either: the code was just completed,
			// or the edit ran through the last box. Changing a digit in the middle
			// of an already-complete code is NOT finished - treating it as finished
			// made every keystroke of a correction fire its own verify request.
			if (this.isComplete(digits) && (!wasComplete || cursor >= this.length)) {
				this.$emit('complete', digits.join(''));
			}
		},

		clearAt(index, focusIndex) {
			const digits = [...this.digits];

			digits[index] = '';
			this.update(digits, focusIndex);
		},

		onInput(index, event) {
			const raw = String(event.target.value || '');
			const characters = raw.replace(/[^0-9]/g, '').split('');

			// Emptied by cut or select-then-delete
			if (raw === '') {
				this.clearAt(index, index);
				return;
			}

			// Non-numeric keystroke: put the digit that was there back on screen
			if (!characters.length) {
				event.target.value = this.digits[index] || '';
				return;
			}

			// The boxes carry no maxlength (so an SMS autofill can drop the whole
			// code into the first one). The side effect is that typing into a box
			// that still holds a digit APPENDS instead of replacing whenever the
			// selection has been lost - e.g. the user clicked the box first. The
			// box then holds two characters and the old digit would be pushed into
			// the next box ("123456" + typing 7 over the first box gave "173456").
			// Treat the digit that was already there as replaced.
			const previous = this.digits[index] || '';

			if (previous && characters.length > 1) {
				const previousAt = characters.indexOf(previous);

				if (previousAt !== -1) {
					characters.splice(previousAt, 1);
				}
			}

			this.write(index, characters);
		},

		onKeydown(index, event) {
			switch (event.key) {
				case 'Backspace':
					event.preventDefault();

					if (this.digits[index]) {
						this.clearAt(index, index);
					} else if (index > 0) {
						this.clearAt(index - 1, index - 1);
					}
					break;

				case 'Delete':
					event.preventDefault();
					this.clearAt(index, index);
					break;

				case 'ArrowLeft':
					event.preventDefault();
					this.focusIndex(index - 1);
					break;

				case 'ArrowRight':
					event.preventDefault();
					this.focusIndex(index + 1);
					break;

				case 'Home':
					event.preventDefault();
					this.focusIndex(0);
					break;

				case 'End':
					event.preventDefault();
					this.focusIndex(this.length - 1);
					break;

				case 'Enter':
					event.preventDefault();
					this.$emit('submit');
					break;

				default:
					break;
			}
		},

		onPaste(index, event) {
			event.preventDefault();

			const clipboard = event.clipboardData || window.clipboardData;
			const text = clipboard ? clipboard.getData('text') : '';
			const characters = String(text || '').replace(/[^0-9]/g, '').split('');

			if (characters.length) {
				this.write(index, characters);
			}
		},

		onFocus(event) {
			// Select whatever is already in the box so the next keystroke
			// overwrites it instead of being swallowed.
			event.target.select();
		},
	},
};
</script>

<style lang="scss" scoped>
.otp-input {
	display: flex;
	justify-content: center;
	gap: 8px;

	&__box {
		width: 44px;
		height: 52px;
		padding: 0;
		border: 1px solid $color-gray-300;
		border-radius: 6px;
		background-color: $color-white;
		color: $color-black-90;
		font-size: rem(24);
		font-weight: 600;
		line-height: 1;
		text-align: center;
		transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

		&:hover:not(:disabled) {
			border-color: $color-gray-400;
		}

		&:focus {
			outline: 0;
			border-color: $color-hypertext;
			box-shadow: 0 0 0 2px rgba($color-hypertext, 0.2);
		}

		&:disabled {
			background-color: $color-gray-100;
			cursor: not-allowed;
		}
	}

	// `:hover:not(:disabled)` and `:focus` out-specify a plain descendant rule,
	// so the error colour has to be restated for those states or the box the
	// cursor happens to sit on silently drops back to the neutral border.
	&--error &__box {
		border-color: $color-alert;

		&:hover:not(:disabled),
		&:focus {
			border-color: $color-alert;
		}

		&:focus {
			box-shadow: 0 0 0 2px rgba($color-alert, 0.2);
		}
	}
}
</style>
